/// <reference types="grecaptcha" />
import { useRef } from 'react';
import { getSDK, recaptchaSDK } from '../ScriptLoader';
import { ENTERPRISE_SITE_KEY, ENTERPRISE_SITE_KEY_V3 } from './constants';
import type { ReCaptchaV3 } from './types';

export type RecaptchaProps = {
  language?: string;
  theme?: ReCaptchaV2.Parameters['theme'];
  size?: ReCaptchaV2.Parameters['size'];
  tabindex?: number;
  verifiedCallback?: (token: string) => void;
  expiredCallback?: () => void;
  errorCallback?: () => void;
  loadedCallback?: () => void;
  useV3?: boolean;
  action?: string;
};

export type RecaptchaReturnType = [
  (elem: HTMLElement | null) => Promise<void>,
  {
    reset: () => Promise<void>;
    execute?: () => Promise<void>;
  },
];

export const useRecaptcha = ({
  language,
  theme,
  size,
  tabindex,
  verifiedCallback,
  expiredCallback,
  errorCallback,
  loadedCallback,
  useV3,
}: RecaptchaProps): RecaptchaReturnType => {
  const cacheResultRef = useRef<RecaptchaReturnType>();

  const widgetIdRef = useRef<number | undefined>(undefined);

  if (cacheResultRef.current) {
    return cacheResultRef.current;
  }

  const initializeV2 = async (elem: HTMLElement) => {
    const sdk = await getSDK<ReCaptchaV2.ReCaptcha>(recaptchaSDK, {
      hl: language,
    });

    widgetIdRef.current = sdk.render(elem, {
      sitekey: ENTERPRISE_SITE_KEY,
      theme,
      size,
      tabindex,
      callback: verifiedCallback,
      'expired-callback': expiredCallback,
      'error-callback': errorCallback,
    });

    if (loadedCallback) {
      sdk.ready(() => {
        let iframe = elem.querySelector('iframe');

        if (iframe) {
          iframe.onload = () => loadedCallback();

          // Fallback: in case iframe already loaded
          try {
            iframe = elem.querySelector('iframe');
            const iframeContent =
              iframe &&
              (iframe.contentDocument ||
                (iframe.contentWindow && iframe.contentWindow.document));

            if (iframeContent) {
              const body = iframeContent.querySelector('body');

              // if body not empty, iframe has loaded
              if (body && body.innerHTML !== '') {
                loadedCallback();
              }
            }
          } catch (err) {
            // If there is an error here, possible cross origin error
            // therefore that means iframe loaded
            loadedCallback();
          }
        } else {
          // Fallback: in case iframe was not found
          loadedCallback();
        }
      });
    }
  };

  const initializeV3 = async () => {
    loadedCallback?.();
    await executeV3();
  };

  const executeV3 = async () => {
    try {
      const sdk = await getSDK<ReCaptchaV3>(recaptchaSDK, {
        hl: language,
        key: ENTERPRISE_SITE_KEY_V3,
        useV3: true,
      });

      sdk.ready(async () => {
        try {
          const token = await sdk.execute(ENTERPRISE_SITE_KEY_V3, {
            action: 'default',
          });
          verifiedCallback?.(token);
        } catch (error) {
          errorCallback?.();
        }
      });
    } catch (error) {
      errorCallback?.();
    } finally {
      loadedCallback?.();
    }
  };

  const ref = async (elem: HTMLElement | null) => {
    if (!elem) {
      return;
    }

    if (widgetIdRef.current !== undefined) {
      await reset();
      widgetIdRef.current = undefined;
    }

    if (useV3) {
      await initializeV3();
    } else {
      await initializeV2(elem);
    }
  };

  const reset = async () => {
    if (useV3) {
      await executeV3();
      return;
    }

    if (typeof widgetIdRef.current !== 'number') {
      return;
    }
    const sdk = await getSDK<ReCaptchaV2.ReCaptcha>(recaptchaSDK, {
      hl: language,
    });
    if (sdk && !!sdk.render) {
      sdk.reset(widgetIdRef.current);
    }
  };

  cacheResultRef.current = [
    ref,
    {
      reset,
      execute: useV3 ? executeV3 : undefined,
    },
  ];

  return cacheResultRef.current;
};
